import type { MemberInvitationResponse, MemberInvite } from '../../shared/contract/orgs.js';
import { elValue } from '../lib/dom.js';
import { MEMBER_ROLES, type MemberRole } from '../lib/member-roles.js';
import { showToast } from '../lib/toast.js';
import { api } from './api.js';
import { loadAssignableMembers, loadMe, loadSettings } from './loaders.js';
import { me, memberDialog, notice } from './state.js';

function checkedRoles(root: Element, name: string): MemberRole[] {
  return Array.from(root.querySelectorAll<HTMLInputElement>(`input[type="checkbox"][name="${name}"]`))
    .filter((box) => box.checked)
    .map((box) => box.value)
    .filter((role): role is MemberRole => MEMBER_ROLES.includes(role as MemberRole));
}

function orgId(): string | null {
  return me.value?.organizationId ?? null;
}

/** Open the invitation dialog from the Members header's "New Member…" (HS2-FJ9J2K). */
export function openInviteDialog(): void {
  memberDialog.value = { kind: 'invite', error: '' };
}

export function closeMemberDialog(): void {
  memberDialog.value = null;
}

/** Send the invitation in the open dialog; a failure stays in the dialog with its message. */
export async function inviteMember(root: HTMLElement): Promise<void> {
  const org = orgId();
  const dialog = root.querySelector('[data-action="member-invite-dialog"]');
  if (org === null || dialog === null || memberDialog.value?.kind !== 'invite') return;
  const email = elValue(dialog.querySelector('[name="member-invite-email"]')).trim().toLowerCase();
  const roles = checkedRoles(dialog, 'member-invite-role');
  if (!email.includes('@') || roles.length === 0) {
    memberDialog.value = { kind: 'invite', error: 'Enter an email and select at least one role.' };
    return;
  }
  const res = await api.inviteMember(org, { email, roles } satisfies MemberInvite);
  if (!res.ok) {
    memberDialog.value = { kind: 'invite', error: res.error ?? 'Could not invite member.' };
    return;
  }
  memberDialog.value = null;
  notice.value = '';
  showToast(invitationToast(email, res.data));
  await loadSettings();
}

/** Tell the admin whether the invitation email went out (HS2-FZQ46C). */
function invitationToast(email: string, result: MemberInvitationResponse | null): string {
  if (result?.emailed === true) return `Invitation emailed to ${email}.`;
  const reason = result?.emailError ?? 'The email was not sent.';
  return `Invitation saved, but not emailed: ${reason} Ask ${email} to sign in with that email.`;
}

/** Open one member's details dialog from their row (HS2-20JG34). */
export function openMemberDialog(memberId: string): void {
  memberDialog.value = { kind: 'edit', memberId, error: '' };
}

/** Show a failed member action inside the open details dialog, or as the page notice otherwise. */
function memberActionFailed(memberId: string, error: string): void {
  const open = memberDialog.value;
  if (open?.kind === 'edit' && open.memberId === memberId) memberDialog.value = { ...open, error };
  else notice.value = error;
}

export async function resendInvitation(memberId: string): Promise<void> {
  const org = orgId();
  if (org === null) return;
  const res = await api.resendInvitation(org, memberId);
  if (!res.ok || res.data === null) {
    memberActionFailed(memberId, res.error ?? 'Could not resend the invitation.');
    return;
  }
  notice.value = '';
  const open = memberDialog.value;
  if (open?.kind === 'edit') memberDialog.value = { ...open, error: '' };
  showToast(invitationToast(res.data.name, res.data));
  await loadSettings();
}

/** Save the roles checked in the open member dialog; success closes it. */
export async function saveMemberRoles(root: Element, memberId: string): Promise<void> {
  const org = orgId();
  const dialog = root.querySelector('[data-action="member-dialog"]');
  if (org === null || dialog === null) return;
  const roles = checkedRoles(dialog, 'member-role');
  if (roles.length === 0) {
    memberActionFailed(memberId, 'Select at least one role.');
    return;
  }
  const res = await api.updateMemberRoles(org, memberId, { roles });
  if (!res.ok) {
    memberActionFailed(memberId, res.error ?? 'Could not update roles.');
    return;
  }
  memberDialog.value = null;
  notice.value = '';
  showToast('Member roles saved.');
  if (memberId === me.value?.memberId) await loadMe();
  await loadSettings();
  // The assignable staff changed; their cleared assignments arrive through the demand change feed (FR-5.20).
  await loadAssignableMembers();
}

export async function removeMember(memberId: string): Promise<void> {
  const org = orgId();
  if (org === null) return;
  if (!window.confirm('Remove this member or cancel their invitation?')) return;
  const res = await api.removeMember(org, memberId);
  if (!res.ok) {
    memberActionFailed(memberId, res.error ?? 'Could not remove member.');
    return;
  }
  memberDialog.value = null;
  notice.value = '';
  showToast('Member removed.');
  if (memberId === me.value?.memberId) await loadMe();
  await loadSettings();
  // The assignable staff changed; their cleared assignments arrive through the demand change feed (FR-5.20).
  await loadAssignableMembers();
}
